import React from "react";
import profTest from "../ProfTest.json";
import {
  SECTION_ORDER,
  computeHollandScores,
  buildHollandResultPayload,
} from "../../helpers/hollandScores";
import { saveHollandResults } from "../../helpers/hollandStorage";

const ProfTestWizard = ({
  compact = false,
  onComplete,
  initialAnswers = null,
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [answers, setAnswers] = React.useState(initialAnswers || {});

  const sections = SECTION_ORDER.map((key) => ({
    key,
    ...profTest.sections[key],
  }));

  const totalSteps = sections.length + 1;

  const handleAnswer = (sectionKey, itemIndex, value) => {
    setAnswers((prev) => {
      const prevSection = prev[sectionKey] || {};
      return {
        ...prev,
        [sectionKey]: {
          ...prevSection,
          [itemIndex]: value,
        },
      };
    });
  };

  const goNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goPrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleComplete = () => {
    const payload = buildHollandResultPayload(answers);
    saveHollandResults(payload);
    onComplete?.(payload);
  };

  const renderSectionStep = (section, index) => {
    const sectionAnswers = answers[section.key] || {};

    return (
      <div className="prof-test-step" key={section.key}>
        <h2>{`${index + 1}. ${section.title}`}</h2>
        <p className="prof-test-instruction">{section.instruction}</p>
        <div className="prof-test-items">
          {section.items.map((item, itemIndex) => {
            const isChecked = !!sectionAnswers[itemIndex];

            return (
              <label
                key={`${section.key}-${item.type}-${item.number}`}
                className={`prof-test-item ${
                  isChecked ? "prof-test-item-checked" : ""
                }`}
              >
                <div className="prof-test-item-left">
                  <span className="prof-test-item-number">
                    {`${item.type}-${item.number}`}
                  </span>
                  <span className="prof-test-item-text">{item.text}</span>
                </div>
                <div className="prof-test-item-controls">
                  <input
                    type="checkbox"
                    className="prof-test-checkbox"
                    checked={isChecked}
                    onChange={(e) =>
                      handleAnswer(section.key, itemIndex, e.target.checked)
                    }
                  />
                  <span className="prof-test-checkbox-visual" />
                </div>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const { scores, hollandCode, sortedTypes } = computeHollandScores(answers);

    return (
      <div className="prof-test-results">
        <h2>Результаты теста</h2>
        <p>
          Ваш предполагаемый код Холланда:{" "}
          <strong>{hollandCode || "—"}</strong>
        </p>
        <p>{profTest.scoring.instructions}</p>
        <div className="prof-test-scores">
          {sortedTypes.map((type) => {
            const typeInfo = profTest.typeDescriptions[type];
            const typeScores = scores[type];

            return (
              <div key={type} className="prof-test-score-card">
                <h3>{`${type} — ${typeInfo ? typeInfo.title : ""} `}</h3>
                <p className="prof-test-score-total">
                  Суммарный балл: <strong>{typeScores.total}</strong>
                </p>
                <ul className="prof-test-score-by-section">
                  {SECTION_ORDER.map((sectionKey) => {
                    const section = profTest.sections[sectionKey];
                    const value = typeScores.bySection[sectionKey] || 0;
                    return (
                      <li key={`${type}-${sectionKey}`}>
                        <strong>{section.title}: </strong>
                        {value}
                      </li>
                    );
                  })}
                </ul>
                {typeInfo ? (
                  <details className="prof-test-type-details">
                    <summary>Описание типа</summary>
                    <p>{typeInfo.description}</p>
                  </details>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const isResultsStep = currentStep === totalSteps - 1;

  return (
    <div
      className={`prof-test-wizard ${compact ? "prof-test-wizard--compact" : ""}`}
    >
      {!compact ? (
        <header className="prof-test-header">
          <h1>{profTest.testTitle}</h1>
          <p className="prof-test-description">{profTest.testDescription}</p>
        </header>
      ) : (
        <header className="prof-test-header prof-test-header--compact">
          <h2>{profTest.testTitle}</h2>
        </header>
      )}
      <div className="prof-test-progress">
        <span>
          Шаг {currentStep + 1} из {totalSteps}
        </span>
      </div>
      <main className="prof-test-main">
        {isResultsStep
          ? renderResults()
          : renderSectionStep(sections[currentStep], currentStep)}
      </main>
      <footer className="prof-test-footer">
        <button
          type="button"
          className="prof-test-nav-btn"
          onClick={goPrev}
          disabled={currentStep === 0}
        >
          Назад
        </button>
        {isResultsStep ? (
          <button
            type="button"
            className="prof-test-nav-btn"
            onClick={handleComplete}
          >
            Сохранить результаты
          </button>
        ) : (
          <button
            type="button"
            className="prof-test-nav-btn"
            onClick={goNext}
          >
            Далее
          </button>
        )}
      </footer>
    </div>
  );
};

export default ProfTestWizard;
